import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { Game } from '../services/backendService/types';
import { gameCacheKey } from './useGame';
import { gamePinCacheKey } from './useGamePin';
import { resultsCacheKey } from './useGames';
import { useMetrics } from './useMetrics';
import { teachingCreditStateKey } from './useTeachingCreditState';

const useEndGame = () => {
  const metrics = useMetrics();
  const client = useQueryClient();
  const {
    mutate: end,
    isPending: endResultIsLoading,
    isSuccess: endResultIsSuccess,
  } = useMutation({
    mutationFn: ({ gameId }: { gameId: string }) => {
      metrics.logEvent('Game.End');
      return BackendService.instance.game.end(gameId);
    },
    onSettled: (_, __, vars) => {
      const game = client.getQueryData<Game>([gameCacheKey, vars.gameId]);
      client.invalidateQueries({
        queryKey: [gameCacheKey, vars.gameId],
      });
      client.invalidateQueries({
        queryKey: [gameCacheKey, vars.gameId, 'success'],
      });
      client.invalidateQueries({
        queryKey: [resultsCacheKey],
        refetchType: 'all',
      });
      client.invalidateQueries({
        queryKey: [gamePinCacheKey, vars.gameId],
      });
      client.invalidateQueries({
        queryKey: teachingCreditStateKey,
      });
      // Invalidate lesson query cache so that the modal doesn't popup again when we end the game.
      if (game?.templateId) {
        client.invalidateQueries({
          queryKey: ['template', game?.templateId],
        });
      }
    },
    onMutate: ({ gameId }) => {
      const cacheKey = [gameCacheKey, gameId];
      client.cancelQueries({
        queryKey: cacheKey,
      });
      const previousGame = client.getQueryData<Game>(cacheKey);
      client.setQueryData<Game | undefined>(cacheKey, oldGame =>
        oldGame
          ? {
              ...oldGame,
              isComplete: true,
              isPlaying: false,
              pin: null,
            }
          : oldGame,
      );
      return () => client.setQueryData(cacheKey, previousGame);
    },
  });

  return {
    end,
    endResultIsLoading,
    endResultIsSuccess,
  };
};

export default useEndGame;
